import { groupBy } from "@ameelio/core";
import { SelectInputBase } from "@ameelio/ui";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GetSystemSatisfactionReportQuery } from "./GetSystemSatisfactionReport.generated.ts";

type GetSystemSatisfactionReportType = Omit<
  GetSystemSatisfactionReportQuery["reports"]["systemSatisfaction"][number],
  "__typename"
>;

const SystemSatisfactionCharts = ({
  data,
}: {
  data: GetSystemSatisfactionReportType[];
}) => {
  const { t } = useTranslation();

  const [systemFilter, setSystemFilter] = useState<string>("all");

  const systemFilterOptions = useMemo(() => {
    const systems = ["all", ...Array.from(new Set(data.map((s) => s.name)))];
    return systems.map((name) => ({
      name: name === "all" ? t("All systems") : name,
      value: name,
    }));
  }, [data, t]);

  const filteredData = useMemo(() => {
    if (!data) return [];
    return !systemFilter || systemFilter === "all"
      ? data
      : data.filter((s) => s.name === systemFilter);
  }, [data, systemFilter]);

  // Group data by system name
  const groupedData = groupBy(filteredData, (r) => r.name);

  // Create chart data for each system
  const createChartData = (systemData: GetSystemSatisfactionReportType[]) => {
    return systemData.map((item) => ({
      userType: item.userType,
      totalRatings: item.ratings,
      badRatings: item.badRatings,
      goodRatings: item.ratings - item.badRatings,
      week: item.yearWeek,
      weekLabel: `${item.userType.charAt(0).toUpperCase()} (${item.yearWeek.split("-").pop()})`,
    }));
  };

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <SelectInputBase
          size="small"
          disabled={!data || !data.length}
          sx={{ maxWidth: 300 }}
          value={systemFilter}
          items={systemFilterOptions}
          label={t("System")}
          onChange={(e) => {
            setSystemFilter(e.target.value as string);
          }}
        />
      </Stack>
      <Box>
        {Object.entries(groupedData).map(([systemName, systemData]) => {
          const chartData = createChartData(systemData || []);
          return (
            <Stack spacing={2} key={systemName}>
              <Typography
                variant="h2"
                sx={{
                  mb: 1,
                }}
              >
                {systemName} System
              </Typography>
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                  }}
                >
                  Ratings by User Type
                </Typography>
                <BarChart
                  dataset={chartData}
                  xAxis={[{ scaleType: "band", dataKey: "weekLabel" }]}
                  series={[
                    {
                      dataKey: "goodRatings",
                      label: "Good Ratings",
                      color: "#4caf50",
                    },
                    {
                      dataKey: "badRatings",
                      label: "Bad Ratings",
                      color: "#f44336",
                    },
                  ]}
                  height={300}
                />
              </Box>
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                  }}
                >
                  Detailed Statistics
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>User Type</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Week</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Total Ratings</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Good Ratings</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Bad Ratings</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Satisfaction %</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chartData.map((item, index) => {
                        const satisfactionPercentage =
                          item.totalRatings > 0
                            ? Math.round(
                                (item.goodRatings / item.totalRatings) * 100
                              )
                            : 0;

                        return (
                          <TableRow
                            // eslint-disable-next-line react/no-array-index-key -- report rows have no stable id
                            key={`${item.userType}_${item.week}_${index}`}
                          >
                            <TableCell>
                              <Chip
                                label={item.userType}
                                size="small"
                                color={
                                  item.userType === "inmate"
                                    ? "primary"
                                    : "secondary"
                                }
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{item.week}</TableCell>
                            <TableCell align="right">
                              {item.totalRatings}
                            </TableCell>
                            <TableCell align="right">
                              {item.goodRatings}
                            </TableCell>
                            <TableCell align="right">
                              {item.badRatings}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${satisfactionPercentage}%`}
                                size="small"
                                color={
                                  satisfactionPercentage >= 80
                                    ? "success"
                                    : satisfactionPercentage >= 60
                                      ? "warning"
                                      : "error"
                                }
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          );
        })}
      </Box>
    </Stack>
  );
};

export default SystemSatisfactionCharts;
